import React from 'react';
import keycloakfetch from '../../functions/keycloakfetch';
import settings from '../../config/settings';
import {ColumnObjectType} from '../global/CustomDataTable';
import {FilterMatchMode, FilterOperator} from 'primereact/api';
import {getGroupedItems} from '../../functions/functionLibrary';
import {CustomConfirmDialog} from '../global/CustomConfirmDialog';
import {getColumn, getFilter, getUserFromLocalStorage, Nullable, sortColumn} from './Global';
import {addMappings, MappingType} from '../../redux/slices/MappingSlice';
import {MappingInputs} from '../globalData/MappingInputs';
import {filterTriStateCheckBox} from '../global/filter';
import moment from 'moment-timezone';


export interface Person {
    'active_id': number,
    'is_deleted': boolean,
    'benutzer': any[],
    'person_id': string,
    'person_dmt_es_id': string,
    'person_vht_id': string,
    'person_verzeichnis_id': string,
    'juristisch_type_id': number,
    'juristisch_type_name': string,
    'juristisch_rechtsform_type_id': number,
    'juristisch_rechtsform_type_name': string,
    'name': string,
    'vorname': string,
    'zweitervorname': string,
    'anrede_type_id': number | null,
    'geburtsdatum': Nullable<Date>,
    'akademischergrad': string,
    'praefix': string,
    'adelstitel': string,
    'spitzname': string,
    'suffix': string,
    'geburtsname': string,
    'geburtsort': string,
    'geburtsort_plz': string,
    'herkunft': string,
    'strassehausnummer': string,
    'ort': string,
    'mobil_telefon': string,
    'e_mail': string,
    'plz': number,
    'kommentar': any,
    'kommentare': any,
    'telefon': string,
    'ersteller': string,
    'zeitstempel': Nullable<Date>,
    'status_id': number,
    'ist_juristisch': boolean,
    'ist_gestorben': boolean,
    'sterbenachweis': string,
    'sterbedatum': Nullable<Date>,
    'sterbeort': string,
    'sterbeort_plz': string,
    'homepage': string,
    'dsgvo_date': Nullable<Date>,
    'datenweitergabe_date': Nullable<Date>,
    'adresse': Address[],
    'bank': Bank[],
    'kontakt': Kontakt[],

    'vorgang'?: string[],
    'flurstueck'?: string[],
    'grundbuch'?: string[],
    'person'?: string[],
    'massnahme'?: string[],
    'inanspruchnahme'?: string[],
    'person_type_id'?: number,
    'anteileigentuemer'?: string,
    'artrechtsgemeinschaft'?: string,
    'miteigentumsanteil'?: string,
    'lfdnr_eintragung'?: string,
    'lfdnr_bestandsverzeichnis'?: string,
    'grundlage_eintragung'?: string,
    'ehepartner'?: string,

    'expandableElement'?: any,
}

export interface Address {
    'abteilung': string,
    'adresse_type_id': number,
    'epsg_code': number,
    'etage': string,
    'gebaeude': string,
    'geo_position': string,
    'ort': string,
    'ortsteil': string,
    'person_id': string,
    'plz': string,
    'strassehausnummer': string,
    'zimmer': string,
    'ladungsadresse': boolean,
    'postadresse': boolean
}

export interface Bank {
    'iban': string,
    'bic': string,
    'bank': string,
    'bank_land': string,
    'swift': string,
    'sap_kreditoren_nummer': string,
    'name': string
}

export interface Kontakt {
    'id': string,
    'person_id': string,
    'kontakt_type_id': number,
    'hauptkontakt': boolean,
    'value': string
}

export interface Parcel {
    'active_id': number,
    'is_deleted': boolean,
    'flurstueck_id': string,
    'flstkennz': string,
    'land_iso': string,
    'land_name': string,
    'b_land_iso': string,
    'b_land_name': string,
    'gemarkung': string,
    'gemarkung_name': string,
    'flstzaehler': string,
    'flstnenner': string,
    'flur': string,
    'flstfolge': string,
    'lfdnrbestandsvz': number,
    'kreis': any,
    'kreis_name': any,
    'gemeinde': string,
    'gemeinde_name': string,
    'kataster_groesse': number,
    'liegenschaft_name': string,
    'liegenschaft_nutzung_types': number[],
    'katasterfortschreibung_type': number,
    'status_id': number,
    'zeitstempel': string,
    'ersteller': string,
    'kommentar': string,
    'kommentare': any,
    'abweichung_grundbuch': number,
    'abweichung_plan': number,
    'abweichung_geo': number,
    'gebucht': boolean,
    'verpachtet': boolean,
    'vertrag': boolean,
    'grundstueckswert'?: Grundstueckswert[],

    'vorgang'?: string[],
    'flurstueck'?: string[],
    'grundbuch'?: string[],
    'person'?: string[],
    'massnahme'?: string[],
    'inanspruchnahme'?: string[],
    'expandableElement'?: any,
    'person_type_id'?: number,
    'anteileigentuemer'?: string,
    'miteigentumsanteil'?: string,
    'artrechtsgemeinschaft'?: string,
    'bvnr'?: number,
    'gr_ha'?: number,
    'gr_a'?: number,
    'gr_m2'?: number,
}

export type Grundstueckswert = {
    'verkehrswert': string,
    'flurstueck_id': string,
    'bewertung_type_id': number,
    'bodenrichtwert': string
}

export type FlstKennz = {
    'b_land_iso': string,
    'b_land_name': string,
    'gemarkung': string,
    'flur': string,
    'flstzaehler': string,
    'flstnenner': string,
    'flstfolge': string
}

export interface LandRegister {
    'active_id': number,
    'grundbuch_id': string,
    'land_iso': string,
    'land_name': string,
    'b_land_iso': string,
    'b_land_name': string,
    'amtsgericht': string,
    'band': string,
    'blatt': string,
    'anteile': string,
    'bezirk': string,
    'gbbz': number,
    'buchungsblattkenn': string,
    'type_id': number,
    'subtype_id': number,
    'letzte_aenderung_zeitstempel': Nullable<Date>,
    'abruf_zeitstempel': Nullable<Date>,
    'pruefung_zeitstempel': Nullable<Date>,
    'kommentar': any[],
    'kommentare': any[],
    'status_id': number,
    'zeitstempel'?: Date,
    'benutzer_id'?: string,

    'vorgang'?: string[],
    'flurstueck'?: string[],
    'massnahme'?: string[],
    'inanspruchnahme'?: string[],
    'person'?: string[],
    'person_type_id'?: number,
    'anteileigentuemer'?: string,
    'artrechtsgemeinschaft'?: string,
    'miteigentumsanteil'?: string,
    'expandableElement'?: any,
    'bvnr'?: number,
    'gr_ha'?: number,
    'gr_a'?: number,
    'gr_m2'?: number,
    'lfdnr_eintragung'?: string,
    'lfdnr_bestandsverzeichnis'?: string,
    'grundlage_eintragung'?: string,
    'ehepartner'?: string,
}

// Process

export interface Process {
    'vorgang_id': string,
    'dmt_es_id': string,
    'status_id': number,
    'active': boolean,
    'land_iso': string,
    'land_name': string,
    'b_land_iso': string,
    'b_land_name': string,
    'kommentar': any,
    'kommentare': any[],
    'vorhabentraeger_id': string,
    'zeitstempel': Date,
}

export interface Correspondence {
    id: string,
    project_id: string,
    person_id: string,
    korrespondenz_type_id: string,
    datum_soll: Nullable<Date>,
    datum_ist: Nullable<Date>,
    benutzer_id: string,
    zeitstempel: Nullable<Date>,
    status: number
}

export interface Contract {
    id: string,
    project_id: string,
    vertrag_type_id: string,
    datum_soll: Nullable<Date>,
    datum_ist: Nullable<Date>,
    flaeche_erwerb: number,
    flaeche_dinglich: number,
    flaeche_temp: number,
    benutzer_id: string,
    zeitstempel: Nullable<Date>,
    status: number
}

const personTypeBody = (rowData: any, personTypes: any) => {
    let result = '';
    if (personTypes) {
        const pt = personTypes.filter((item: any) => {
            return item.value === rowData['person_type_id'];
        });

        if (pt) {
            result = pt.map((item: any) => {
                return item.label;
            });
        }
    }
    return <div>{result}</div>;
};

const correspondenceTypeBody = (rowData: any, personTypes: any) => {
    let result = '';
    if (personTypes) {
        const pt = personTypes.filter((item: any) => {
            return item.value === rowData['korrespondenz_type_id'];
        });

        if (pt) {
            result = pt.map((item: any) => {
                return item.label;
            });
        }
    }
    return <div>{result}</div>;
};

const contractTypeBody = (rowData: any, contractTypes: any) => {
    let result = '';
    if (contractTypes) {
        console.log(contractTypes);
        const pt = contractTypes.filter((item: any) => {
            return item.value === rowData['vertrag_type_id'];
        });
        if (pt) {
            result = pt.map((item: any) => {
                return item.label;
            });
        }
    }
    return <div>{result}</div>;
};

export async function saveCSV(projectId: string, csv: any, overwrite_person: boolean, overwrite_parcel: boolean) {
    let retValue: any = [];
    const data = {
        'project_id': projectId ? projectId : '',
        data: csv,
        overwrite_person: overwrite_person,
        overwrite_parcel: overwrite_parcel
    };
    await keycloakfetch.post(settings.apiUrl + '/importALKIS', data).then((result) => {
        if (result)
            retValue = result;
    });
    return retValue;
}

export const getColumnsParcel = (parcels: any, t: any, personTypes: any[], showPersons: boolean, showLandRegisters: boolean, mapping: MappingType, showUtilisationInfo: boolean) => {
    const temp = [];

    const filterCheckboxIsVisible = (options: any) => {
        return filterTriStateCheckBox(options, t('showAll'), t('yes'), t('no'));
    };

    temp.push(
        {
            field: 'status_id',
            header: t('globalData:process.columns.status_id'),
            filter: true,
            display: true,
            filterField: 'status_id',
            type: ColumnObjectType.StatusLights,
            style: {minWidth: '100px', maxWidth: '100px'},
        },
        {
            field: 'flstkennz',
            header: t('globalData:parcels.flstkennz'),
            filter: true,
            display: true
        });

    if (showPersons) {
        temp.push({
            field: 'person',
            header: t('globalData:global.anzahlpersonen'),
            body: (rowData: any) => {
                if (rowData['person']) {
                    return rowData['person'][0] === null ? 0 : rowData['person'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    if (showLandRegisters) {
        temp.push({
            field: 'grundbuch',
            header: t('globalData:global.anzahllandRegisters'),
            body: (rowData: any) => {
                if (rowData['grundbuch']) {
                    return rowData['grundbuch'][0] === null ? 0 : rowData['grundbuch'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    if (showUtilisationInfo) {
        temp.push({
            field: 'inanspruchnahme',
            header: t('projectMasterData:utilisation.plural'),
            body: (rowData: any) => {
                if (rowData['inanspruchnahme']) {
                    return rowData['inanspruchnahme'][0] === null ? 0 : rowData['inanspruchnahme'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    switch (mapping) {
        case MappingType.landRegisterParcel:
            temp.push({
                field: 'bvnr',
                header: t('globalData:parcels.bvnr'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_ha',
                header: t('globalData:parcels.gr_ha'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_a',
                header: t('globalData:parcels.gr_a'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_m2',
                header: t('globalData:parcels.gr_m2'),
                filter: true,
                display: true,
            },);
            break;

        case MappingType.personParcel:
            temp.push({
                field: 'person_type_id',
                header: t('globalData:columns.personType'),
                filter: true,
                filterField: 'person_type_id',
                filterDropdownValues: personTypes ? personTypes.map((item) => {
                    return {value: item.value, name: item.label};
                }) : [],
                display: true,
                body: (rowData: any) => {
                    return personTypeBody(rowData, personTypes);
                }
            },);
            break;
    }

    temp.push({
            field: 'kreis',
            header: t('globalData:parcels.kreis'),
            filter: true,
            display: false
        }, {
            field: 'kreis_name',
            header: t('globalData:parcels.kreis_name'),
            filter: true,
            display: true
        }, {
            field: 'gemeinde',
            header: t('globalData:parcels.gemeinde'),
            filter: true,
            display: false
        }, {
            field: 'gemeinde_name',
            header: t('globalData:parcels.gemeinde_name'),
            filter: true,
            display: true
        },
        {
            field: 'gemarkung',
            header: t('globalData:parcels.gemarkung'),
            filter: true,
            display: false
        }, {
            field: 'gemarkung_name',
            header: t('globalData:parcels.gemarkung_name'),
            filter: true,
            display: true
        }, {
            field: 'flur',
            header: t('globalData:parcels.flur'),
            filter: true,
            display: true
        }, {
            field: 'flstzaehler',
            header: t('globalData:parcels.flstzaehler'),
            filter: true,
            display: true
        }, {
            field: 'flstnenner',
            header: t('globalData:parcels.flstnenner'),
            filter: true,
            display: true
        }, {
            field: 'kataster_groesse',
            header: t('globalData:parcels.katasterGroesse'),
            filter: true,
            display: true
        }, {
            field: 'flurstueck_id',
            header: 'Id',
            filter: false,
            display: false
        }, {
            field: 'flstfolge',
            header: t('globalData:parcels.flstfolge'),
            filter: true,
            display: false
        }, {
            field: 'b_land_name',
            header: t('globalData:parcels.stateIso'),
            filter: true,
            filterField: 'b_land_iso',
            filterDropdownValues: parcels ? getGroupedItems(parcels, 'b_land_iso', 'b_land_name') : undefined,
            showFilterMatchModes: false,
            display: false,
            body: (rowData: any) => {
                return (<div className={'flex'}><img className={'mr-1'}
                                                     src={`/images/wappen/${rowData['b_land_iso']}.svg`}
                                                     width={20}
                                                     height={20}/>
                    <div className={'my-auto'}>{rowData['b_land_name']}</div>
                </div>);
            },
        }, {
            field: 'land_name',
            header: t('globalData:parcels.countryIso'),
            filter: true,
            filterField: 'land_iso',
            filterDropdownValues: parcels ? getGroupedItems(parcels, 'land_iso', 'land_name') : undefined,
            showFilterMatchModes: false,
            display: false,
            body: (rowData: any) => {
                return (<div className={'flex'}>
                    {rowData['land_name'] && <img className={'mr-1'}
                                                  src={`/images/wappen/${rowData['land_name'].toLowerCase()}.svg`}
                                                  width={20}
                                                  height={20}/>}
                    <div className={'my-auto'}>{rowData['land_name']}</div>
                </div>);
            },
        }, {
            field: 'nutzung_type',
            header: t('globalData:parcels.nutzungType'),
            filter: true,
            display: false
        }, {
            field: 'lfdnrbestandsvz',
            header: t('globalData:parcels.lfdnrbestandsvz'),
            style: {maxWidth: '30px'},
            filter: true,
            display: false
        }, {
            field: 'gebucht',
            header: t('globalData:parcels.gebucht'),
            style: {maxWidth: '30px'},
            filter: true,
            filterField: 'gebucht',
            filterElement: filterCheckboxIsVisible,
            display: false,
            type: ColumnObjectType.Boolean
        },
        {
            field: 'verpachtet',
            header: t('globalData:parcels.verpachtet'),
            style: {maxWidth: '30px'},
            filter: true,
            filterField: 'verpachtet',
            filterElement: filterCheckboxIsVisible,
            display: false,
            type: ColumnObjectType.Boolean
        },
        {
            field: 'vertrag',
            header: t('globalData:parcels.vertrag'),
            style: {maxWidth: '30px'},
            filter: true,
            filterField: 'vertrag',
            filterElement: filterCheckboxIsVisible,
            display: false,
            type: ColumnObjectType.Boolean
        }, {
            field: 'kommentar',
            header: t('globalData:columns.comment'),
            filter: true,
            filterField: 'kommentar',
            display: false
        }, {
            field: 'zeitstempel',
            header: t('globalData:columns.timestamp'),
            filter: true,
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: false
        }, {
            field: 'ersteller',
            header: t('globalData:persons.author'),
            filter: true,
            filterField: 'ersteller',
            display: false
        }, {
            field: 'active_id',
            header: t('globalData:columns.zustand'),
            filter: true,
            filterDropdownValues: [
                {value: 0, name: t('globalData:columns.zustand0')},
                {value: 1, name: t('globalData:columns.zustand1')},
                {value: 2, name: t('globalData:columns.zustand2')},
            ],
            display: false,
            body: (rowData: any) => {
                return (<div>{t('globalData:columns.zustand' + rowData['active_id'])}</div>);
            },
        }, {
            field: 'project',
            header: t('globalData:columns.projects'),
            filter: false,
            display: false,
            body: (rowData: any) => {
                if (rowData['project']) {
                    return rowData['project'][0] === null ? 0 : rowData['project'].length;
                }
            },
            sortFunction: sortColumn
        });

    return temp;
};

export const getFiltersParcel = () => {
    return {
        'flstkennz': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'flstfolge': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'flstnenner': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'flstzaehler': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'flur': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'amtsgericht': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'grundbuchbezirk': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gbbz': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'blatt': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'lfdnrbestandsvz': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gebucht': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'verpachtet': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'vertrag': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'buchungsart': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'b_land_iso': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'land_iso': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'zeitstempel_1': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'status_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]
        },
        'active_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'kataster_groesse': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'gemeinde': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'gemeinde_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'kreis': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'kreis_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'nutzung_type': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        // 'miteigentumsanteil': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'artrechtsgemeinschaft': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'anteileigentuemer': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        'person_type_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'bvnr': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_ha': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_a': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_m2': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        }
    };
};

export const getColumnsPerson = (persons: any, t: any, personTypes: any[], commonData: boolean, showSuggestions: boolean, showParcels: boolean, showPersons: boolean, showLandRegisters: boolean, mapping: MappingType) => {
    // console.log(persons, persons, commonData, showSuggestions, showParcels, showPersons, showLandRegisters, showShareInfos)
    const temp = [];

    const timezone = getUserFromLocalStorage().timezone;
    const filterCheckboxIsVisible = (options: any) => {
        return filterTriStateCheckBox(options, t('showAll'), t('yes'), t('no'));
    };

    temp.push(
        {
            field: 'status_id',
            header: t('globalData:process.columns.status_id'),
            filter: true,
            display: true,
            filterField: 'status_id',
            type: ColumnObjectType.StatusLights,
            style: {minWidth: '100px', maxWidth: '100px'},
        },
        {
            field: 'person_dmt_es_id',
            header: t('globalData:persons.personDmtEsId'),
            filter: true,
            filterField: 'person_dmt_es_id',
            display: true
        }, {
            field: 'person_vht_id',
            header: t('globalData:persons.personVhtId'),
            filter: true,
            filterField: 'person_vht_id',
            display: true
        });

    switch (mapping) {
        case MappingType.personParcel:
            temp.push({
                field: 'person_type_id',
                header: t('globalData:columns.personType'),
                filter: true,
                filterField: 'person_type_id',
                filterDropdownValues: personTypes ? personTypes.map((item) => {
                    return {value: item.value, name: item.label};
                }) : [],
                display: true,
                body: (rowData: any) => {
                    return personTypeBody(rowData, personTypes);
                }
            },);
            // temp.push({
            //     field: 'anteileigentuemer',
            //     header: t('globalData:persons.anteileigentuemer'),
            //     filter: true,
            //     display: true,
            // },);
            // temp.push({
            //     field: 'miteigentumsanteil',
            //     header: t('globalData:parcels.miteigentumsanteil'),
            //     filter: true,
            //     display: true,
            // },);
            // temp.push({
            //     field: 'artrechtsgemeinschaft',
            //     header: t('globalData:parcels.artrechtsgemeinschaft'),
            //     filter: true,
            //     display: true,
            // },);
            break;

        case MappingType.personPerson:
            temp.push({
                field: 'person_type_id',
                header: t('globalData:columns.personType'),
                filter: true,
                filterField: 'person_type_id',
                filterDropdownValues: personTypes ? personTypes.map((item) => {
                    return {value: item.value, name: item.label};
                }) : [],
                display: true,
                body: (rowData: any) => {
                    return personTypeBody(rowData, personTypes);
                }
            },);
            break;

        case MappingType.personLandRegister:
            temp.push({
                field: 'person_type_id',
                header: t('globalData:columns.personType'),
                filter: true,
                filterField: 'person_type_id',
                filterDropdownValues: personTypes ? personTypes.map((item) => {
                    return {value: item.value, name: item.label};
                }) : [],
                display: true,
                body: (rowData: any) => {
                    return personTypeBody(rowData, personTypes);
                }
            },);
            temp.push({
                field: 'anteileigentuemer',
                header: t('globalData:persons.anteileigentuemer'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'miteigentumsanteil',
                header: t('globalData:parcels.miteigentumsanteil'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'artrechtsgemeinschaft',
                header: t('globalData:parcels.artrechtsgemeinschaft'),
                filter: true,
                display: true,
            },);
            break;
    }

    if (showParcels) {
        temp.push({
            field: 'flurstueck',
            header: t('globalData:global.anzahlflurstuecke'),
            body: (rowData: any) => {
                if (rowData['flurstueck']) {
                    return rowData['flurstueck'][0] === null ? 0 : rowData['flurstueck'].length;
                }
            },
            sortFunction: sortColumn,
            display: false
        },);
    }

    if (showPersons) {
        temp.push({
            field: 'person',
            header: t('globalData:global.mapping.vertretungFuer'),
            body: (rowData: any) => {
                if (rowData['person']) {
                    return rowData['person'][0] === null ? 0 : rowData['person'].length;
                }
            },
            sortFunction: sortColumn,
            display: false
        },);
    }

    if (showLandRegisters) {
        temp.push({
            field: 'grundbuch',
            header: t('globalData:global.anzahllandRegisters'),
            body: (rowData: any) => {
                if (rowData['grundbuch']) {
                    return rowData['grundbuch'][0] === null ? 0 : rowData['grundbuch'].length;
                }
            },
            sortFunction: sortColumn,
            display: false
        },);
    }

    temp.push({
            field: 'ist_juristisch',
            header: t('globalData:persons.ist_juristisch'),
            filter: true,
            filterField: 'ist_juristisch',
            filterElement: filterCheckboxIsVisible,
            type: ColumnObjectType.Boolean,
            display: true
        }, {
            field: 'name',
            header: t('globalData:persons.name'),
            filter: true,
            filterField: 'name',
            display: true
        }, {
            field: 'vorname',
            header: t('globalData:persons.firstName'),
            filter: true,
            filterField: 'vorname',
            display: true
        }, {
            field: 'zweitervorname',
            header: t('globalData:persons.middleName'),
            filter: true,
            filterField: 'zweitervorname',
            display: true
        },
        {
            field: 'spitzname',
            header: t('globalData:persons.spitzname'),
            filter: true,
            filterField: 'spitzname',
            display: false
        }, {
            field: 'geburtsdatum',
            header: t('globalData:persons.birthDate'),
            filter: true,
            filterField: 'geburtsdatum',
            type: ColumnObjectType.Date,
            // dateFormat: t('dateformat'),
            display: true,
            body: (rowData: any) => {
                let value = '';
                if (rowData['geburtsdatum']) {
                    value = '* ' + moment(rowData['geburtsdatum']).tz(timezone).format(t('dateformat'));
                }
                return (<div>{value}</div>);
            },
        }, {
            field: 'sterbedatum',
            header: t('globalData:persons.sterbedatum'),
            filter: true,
            filterField: 'sterbedatum',
            type: ColumnObjectType.Date,
            // dateFormat: t('dateformat'),
            display: true,
            body: (rowData: any) => {
                let value = '';
                if (rowData['sterbedatum']) {
                    value = '+ ' + moment(rowData['sterbedatum']).tz(timezone).format(t('dateformat'));
                }
                return (<div>{value}</div>);
            },
        }, {
            field: 'adresse_post_strassehausnummer',
            header: t('globalData:persons.streetHouseNrPost'),
            filter: true,
            filterField: 'adresse_post_strassehausnummer',
            display: true
        }, {
            field: 'adresse_post_plz',
            header: t('globalData:persons.plzPost'),
            filter: true,
            style: {maxWidth: '50px'},
            filterField: 'adresse_post_plz',
            filterDropdownValues: persons ? getGroupedItems(persons, 'adresse_post_plz', 'adresse_post_plz') : undefined,
            showFilterMatchModes: false,
            display: true
        }, {
            field: 'adresse_post_ort',
            header: t('globalData:persons.cityPost'),
            filter: true,
            filterField: 'adresse_post_ort',
            filterDropdownValues: persons ? getGroupedItems(persons, 'adreses_post_ort', 'adresse_post_ort') : undefined,
            showFilterMatchModes: false,
            display: true
        }, {
            field: 'person_id',
            header: 'Id',
            filter: false,
            display: false
        }, {
            field: 'juristisch_type_name',
            header: t('globalData:persons.juristisch_type_name'),
            filter: true,
            filterField: 'juristisch_type_name',
            type: ColumnObjectType.Text,
            display: false
        }, {
            field: 'juristisch_rechtsform_type_name',
            header: t('globalData:persons.juristisch_rechtsform_type_name'),
            filter: true,
            filterField: 'juristisch_rechtsform_type_name',
            type: ColumnObjectType.Text,
            display: false
        }, {
            field: 'akademischergrad',
            header: t('globalData:persons.academicaldegree'),
            filter: true,
            style: {maxWidth: '40px'},
            filterField: 'akademischergrad',
            display: false
        }, {
            field: 'namensbestandteil',
            header: t('globalData:persons.namePart'),
            filter: true,
            style: {maxWidth: '60px'},
            filterField: 'namensbestandteil',
            display: false
        }, {
            field: 'geburtsname',
            header: t('globalData:persons.birthName'),
            filter: true,
            filterField: 'geburtsname',
            display: false
        }, {
            field: 'geburtsort',
            header: t('globalData:persons.birthPlace'),
            filter: true,
            filterField: 'geburtsort',
            display: false
        }, {
            field: 'geburtsort_plz',
            header: t('globalData:persons.birthPlacePlz'),
            filter: true,
            filterField: 'geburtsort_plz',
            display: false
        }, {
            field: 'adresse_ladung_strassehausnummer',
            header: t('globalData:persons.streetHouseNrLoad'),
            filter: true,
            filterField: 'adresse_ladung_strassehausnummer',
            display: false
        }, {
            field: 'adresse_ladung_plz',
            header: t('globalData:persons.plzLoad'),
            filter: true,
            style: {maxWidth: '50px'},
            filterField: 'adresse_ladung_plz',
            filterDropdownValues: persons ? getGroupedItems(persons, 'adresse_ladung_plz', 'adresse_ladung_plz') : undefined,
            showFilterMatchModes: false,
            display: false
        }, {
            field: 'adresse_ladung_ort',
            header: t('globalData:persons.cityLoad'),
            filter: true,
            filterField: 'adresse_ladung_ort',
            filterDropdownValues: persons ? getGroupedItems(persons, 'adresse_ladung_ort', 'adresse_ladung_ort') : undefined,
            showFilterMatchModes: false,
            display: false
        }, {
            field: 'e_mail',
            header: t('globalData:persons.email'),
            filter: true,
            filterField: 'e_mail',
            display: false
        }, {
            field: 'mobil_telefon',
            header: t('globalData:persons.mobilPhone'),
            filter: true,
            filterField: 'mobil_telefon',
            display: false
        }, {
            field: 'telefon',
            header: t('globalData:persons.phone'),
            filter: true,
            filterField: 'telefon',
            display: false
        }, {
            field: 'homepage',
            header: t('globalData:persons.homepage'),
            filter: true,
            filterField: 'homepage',
            display: false
        }, {
            field: 'kommentar',
            header: t('globalData:columns.comment'),
            filter: true,
            filterField: 'kommentar',
            display: false
        }, {
            field: 'ist_gestorben',
            header: t('globalData:persons.ist_gestorben'),
            filter: true,
            filterField: 'ist_gestorben',
            filterElement: filterCheckboxIsVisible,
            type: ColumnObjectType.Boolean,
            display: false
        }, {
            field: 'sterbeort',
            header: t('globalData:persons.sterbeort'),
            filter: true,
            filterField: 'sterbeort',
            type: ColumnObjectType.Text,
            display: false
        }, {
            field: 'sterbeort_plz',
            header: t('globalData:persons.sterbeort_plz'),
            filter: true,
            filterField: 'sterbeort_plz',
            type: ColumnObjectType.Text,
            display: false
        }, {
            field: 'herkunft',
            header: t('globalData:persons.originData'),
            filter: true,
            filterField: 'herkunft',
            display: false
        }, {
            field: 'erstioneller',
            header: t('globalData:persons.author'),
            filter: true,
            filterField: 'ersteller',
            display: false
        }, {
            field: 'zeitstempel',
            header: t('globalData:columns.timestamp'),
            filter: true,
            filterField: 'zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: false,
        }, {
            field: 'active_id',
            header: t('globalData:columns.zustand'),
            filter: true,
            filterDropdownValues: [
                {value: 0, name: t('globalData:columns.zustand0')},
                {value: 1, name: t('globalData:columns.zustand1')},
                {value: 2, name: t('globalData:columns.zustand2')},
            ],
            display: false,
            body: (rowData: any) => {
                return (<div>{t('globalData:columns.zustand' + rowData['active_id'])}</div>);
            },
        }, {
            field: 'project',
            header: t('globalData:columns.projects'),
            filter: false,
            display: false,
            body: (rowData: any) => {
                if (rowData['project']) {
                    return rowData['project'][0] === null ? 0 : rowData['project'].length;
                }
            },
            sortFunction: sortColumn
        },
        {
            field: 'user_sachbearbeiter',
            header: t('globalData:process.sachbearbeiter'),
            filter: true,
            // filterField: 'user_sachbearbeiter',
            // filterDropdownValues: persons ? getGroupedItems(persons, 'user_sachbearbeiter', 'user_sachbearbeiter') : undefined,
            // showFilterMatchModes: false,
            display: false,
        },
        {
            field: 'user_verhandler',
            header: t('globalData:process.verhandler'),
            filter: true,
            // filterField: 'user_verhandler',
            // filterDropdownValues: persons ? getGroupedItems(persons, 'user_verhandler', 'user_verhandler') : undefined,
            // showFilterMatchModes: false,
            display: false,
        });

    if (!commonData && showSuggestions) {
        temp.push({
            field: 'old_person_id',
            header: t('globalData:persons.suggestions'),
            display: true,
            body: (rowData: any) => {
                if (rowData['old_person_id']) {
                    return rowData['old_person_id'].length;
                }
            },
            sortFunction: sortColumn
        });
        temp.push({
            field: 'do_insert',
            header: t('globalData:persons.do_insert'),
            display: true,
            // body: (rowData: any) => {
            //     return (<div style={{textAlign: 'center'}}>
            //     <FontAwesomeIcon
            //         icon={rowData['do_insert'] === null ? faSlash : rowData['do_insert'] ? faCircleCheck : faCircleXmark}
            //     className="m-2"
            //         />
            //         </div>);
            // }
        });
    }

    // console.log(temp.concat(temp2))
    return temp;
};

export const getFiltersPerson = () => {
    return {
        'juristisch_type_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'juristisch_rechtsform_type_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'akademischergrad': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'namensbestandteil': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'vorname': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'zweitervorname': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'spitzname': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'geburtsname': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'geburtsdatum': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'adresse_ladung_strassehausnummer': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'adresse_ladung_plz': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'adresse_ladung_ort': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'adresse_post_strassehausnummer': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'adresse_post_plz': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'adresse_post_ort': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'e_mail': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'mobil_telefon': {
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'telefon': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'kommentar': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'ersteller': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'zeitstempel': {
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'miteigentumsanteil': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'artrechtsgemeinschaft': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'anteileigentuemer': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'ist_gestorben': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'sterbedatum': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'ist_juristisch': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'homepage': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'status_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]
        },
        'person_type_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'user_sachbearbeiter': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'user_verhandler': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
    };
};

export const getColumnsLandRegister = (landRegisters: LandRegister[], personTypes: any[], t: any, showParcels: boolean, showPersons: boolean, mapping: MappingType) => {
    const temp: any[] = [];

    temp.push(
        {
            field: 'status_id',
            header: t('globalData:process.columns.status_id'),
            filter: true,
            display: true,
            filterField: 'status_id',
            type: ColumnObjectType.StatusLights,
            style: {minWidth: '100px', maxWidth: '100px'},
        }, {
            field: 'grundbuch_id',
            header: t('globalData:landRegister.id'),
            filter: false,
            display: true
        });

    if (showPersons) {
        temp.push({
            field: 'person',
            header: t('globalData:global.personen'),
            body: (rowData: any) => {
                if (rowData['person']) {
                    return rowData['person'][0] === null ? 0 : rowData['person'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    if (showParcels) {
        temp.push({
            field: 'flurstueck',
            header: t('globalData:global.flurstuecke'),
            body: (rowData: any) => {
                if (rowData['flurstueck']) {
                    return rowData['flurstueck'][0] === null ? 0 : rowData['flurstueck'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    switch (mapping) {
        case MappingType.landRegisterParcel:
            temp.push({
                field: 'bvnr',
                header: t('globalData:parcels.bvnr'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_ha',
                header: t('globalData:parcels.gr_ha'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_a',
                header: t('globalData:parcels.gr_a'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'gr_m2',
                header: t('globalData:parcels.gr_m2'),
                filter: true,
                display: true,
            },);
            break;

        case MappingType.personLandRegister:
            temp.push({
                field: 'person_type_id',
                header: t('globalData:columns.personType'),
                filter: true,
                filterField: 'person_type_id',
                filterDropdownValues: personTypes ? personTypes.map((item) => {
                    return {value: item.value, name: item.label};
                }) : [],
                display: true,
                body: (rowData: any) => {
                    return personTypeBody(rowData, personTypes);
                }
            },);
            temp.push({
                field: 'anteileigentuemer',
                header: t('globalData:persons.anteileigentuemer'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'miteigentumsanteil',
                header: t('globalData:parcels.miteigentumsanteil'),
                filter: true,
                display: true,
            },);
            temp.push({
                field: 'artrechtsgemeinschaft',
                header: t('globalData:parcels.artrechtsgemeinschaft'),
                filter: true,
                display: true,
            },);
            break;
    }

    temp.push({
            field: 'amtsgericht',
            header: t('globalData:landRegister.amtsgericht'),
            filter: true,
            filterField: 'amtsgericht',
            display: true
        }, {
            field: 'bezirk',
            header: t('globalData:landRegister.bezirk'),
            filter: true,
            filterField: 'bezirk',
            display: true
        }, {
            field: 'band',
            header: t('globalData:landRegister.band'),
            filter: true,
            filterField: 'band',
            display: true
        },
        {
            field: 'blatt',
            header: t('globalData:landRegister.blatt'),
            filter: true,
            filterField: 'blatt',
            display: true
        }, {
            field: 'letzte_aenderung_zeitstempel',
            header: t('globalData:landRegister.letzte_aenderung_zeitstempel'),
            filter: true,
            filterField: 'letzte_aenderung_zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: true
        },
        {
            field: 'abruf_zeitstempel',
            header: t('globalData:landRegister.abruf_zeitstempel'),
            filter: true,
            filterField: 'abruf_zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: true
        },
        {
            field: 'pruefung_zeitstempel',
            header: t('globalData:landRegister.pruefung_zeitstempel'),
            filter: true,
            filterField: 'pruefung_zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: true
        },
        {
            field: 'b_land_name',
            header: t('globalData:parcels.stateIso'),
            filter: true,
            filterField: 'b_land_iso',
            filterDropdownValues: landRegisters ? getGroupedItems(landRegisters, 'b_land_iso', 'b_land_name') : undefined,
            showFilterMatchModes: false,
            display: false,
            body: (rowData: any) => {
                return (<div className={'flex'}><img className={'mr-1'}
                                                     src={`/images/wappen/${rowData['b_land_iso']}.svg`}
                                                     width={20}
                                                     height={20}/>
                    <div className={'my-auto'}>{rowData['b_land_name']}</div>
                </div>);
            },
        }, {
            field: 'land_name',
            header: t('globalData:parcels.countryIso'),
            filter: true,
            filterField: 'land_iso',
            filterDropdownValues: landRegisters ? getGroupedItems(landRegisters, 'land_iso', 'land_name') : undefined,
            showFilterMatchModes: false,
            display: false,
            body: (rowData: any) => {
                return (<div className={'flex'}>
                    {rowData['land_name'] && <img className={'mr-1'}
                                                  src={`/images/wappen/${rowData['land_name'].toLowerCase()}.svg`}
                                                  width={20}
                                                  height={20}/>}
                    <div className={'my-auto'}>{rowData['land_name']}</div>
                </div>);
            },
        },

        // {
        //     field: 'blatt',
        //     header: t('globalData:landRegister.blatt'),
        //     filter: true,
        //     filterField: 'blatt',
        //     display: true
        // },

        // {
        //     field: 'bvnr',
        //     header: t('globalData:landRegister.bvnr'),
        //     filter: true,
        //     filterField: 'bvnr',
        //     display: true
        // },
        // {
        //     field: 'buchungsblattkenn',
        //     header: t('globalData:landRegister.buchungsblattkenn'),
        //     filter: true,
        //     filterField: 'buchungsblattkenn',
        //     display: true
        // },
        // {
        //     field: 'gbbz',
        //     header: t('globalData:landRegister.gbbz'),
        //     filter: true,
        //     filterField: 'gbbz',
        //     display: true
        // },
        // {
        //     field: 'kommentar',
        //     header: t('globalData:columns.comment'),
        //     filter: true,
        //     filterField: 'kommentar',
        //     display: false
        // },
        {
            field: 'zeitstempel',
            header: t('globalData:columns.timestamp'),
            filter: true,
            filterField: 'zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: false
        }, {
            field: 'type_name',
            header: t('globalData:landRegister.type_name'),
            filter: true,
            filterField: 'type_name',
            display: false
        }, {
            field: 'active_id',
            header: t('globalData:columns.zustand'),
            filter: true,
            filterDropdownValues: [
                {value: 0, name: t('globalData:columns.zustand0')},
                {value: 1, name: t('globalData:columns.zustand1')},
                {value: 2, name: t('globalData:columns.zustand2')},
            ],
            display: false,
            body: (rowData: any) => {
                return (<div>{t('globalData:columns.zustand' + rowData['active_id'])}</div>);
            },
        });

    return temp;
};

export const getFiltersLandRegister = () => {
    return {
        'amtsgericht': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        // 'blatt': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'bvnr': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'anteil': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'bemerkung': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        'bezirk': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        // 'gbbz': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'buchungsart': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'buchungsblattkenn': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        // 'kommentar': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
        'letzte_aenderung_zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'abruf_zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'pruefung_zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'type_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'status_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]
        },
        'bvnr': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_ha': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_a': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'gr_m2': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        }
    };
};

export const getColumnsProjectList = (t: any) => {
    const temp = [];

    temp.push({
        field: 'alibi_id',
        header: 'Alibi Id',
        filter: false,
        display: false
    }, {
        field: 'baselayer_workspace',
        header: t('globalData:columns.baselayer_workspace'),
        filter: false,
        display: false
    }, {
        field: 'comment',
        header: t('globalData:columns.comment'),
        filter: false,
        display: false
    }, {
        field: 'customer_address',
        header: t('globalData:columns.customer_address'),
        filter: false,
        display: false
    }, {
        field: 'customer_name',
        header: t('globalData:columns.customer_name'),
        filter: false,
        display: false
    }, {
        field: 'geoserver_workspace',
        header: t('globalData:columns.geoserver_workspace'),
        filter: false,
        display: false
    }, {
        field: 'id',
        header: 'Id',
        filter: false,
        display: false
    }, {
        field: 'is_active',
        header: t('globalData:columns.is_active'),
        filter: false,
        type: ColumnObjectType.Boolean,
        display: true
    }, {
        field: 'name',
        header: t('globalData:columns.name'),
        filter: false,
        display: true
    }, {
        field: 'project_type_id',
        header: t('globalData:columns.project_type_id'),
        filter: false,
        display: false
    }, {
        field: 'wiki_url',
        header: t('globalData:columns.wiki_url'),
        filter: false,
        display: false
    });

    return temp;
};

export const getFiltersProjectList = () => {
    return {
        'alibi_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'baselayer_workspace': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'comment': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'customer_address': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'customer_name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'geoserver_workspace': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'is_active': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'name': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'project_type_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'wiki_url': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
    };
};


export const getColumnsProcess = (t: any, process: any, showParcels: boolean, showPersons: boolean, showLandRegisters: boolean) => {
    const temp = [];

    const filterCheckboxIsVisible = (options: any) => {
        return filterTriStateCheckBox(options, t('showAll'), t('yes'), t('no'));
    };

    temp.push(
        {
            field: 'status_id',
            header: t('globalData:process.columns.status_id'),
            filter: true,
            display: true,
            filterField: 'status_id',
            type: ColumnObjectType.StatusLights,
            style: {minWidth: '100px', maxWidth: '100px'},
        },
        {
            field: 'active',
            header: t('active'),
            filter: true,
            filterField: 'active',
            filterElement: filterCheckboxIsVisible,
            type: ColumnObjectType.Boolean,
            display: false
        },
        // {
        //     field: 'id',
        //     header: t('globalData:process.columns.id'),
        //     filter: true,
        //     filterField: 'id',
        //     display: false,
        // },
        {
            field: 'dmt_es_id',
            header: t('globalData:process.columns.dmt_es_id'),
            filter: true,
            filterField: 'dmt_es_id',
            display: true
        },
        {
            field: 'vorhabentraeger_id',
            header: t('globalData:process.columns.vorhabentraeger_id'),
            filter: true,
            filterField: 'vorhabentraeger_id',
            display: false
        },
        {
            field: 'zeitstempel',
            header: t('globalData:process.columns.timestamp'),
            filter: true,
            filterField: 'zeitstempel',
            type: ColumnObjectType.Date,
            dateFormat: t('datetimeformat'),
            display: false
        },
        {
            field: 'b_land_name',
            header: t('globalData:parcels.stateIso'),
            filter: true,
            filterField: 'b_land_iso',
            filterDropdownValues: process ? getGroupedItems(process, 'b_land_iso', 'b_land_name') : undefined,
            showFilterMatchModes: false,
            display: true,
            body: (rowData: any) => {
                return (<div className={'flex'}><img className={'mr-1'}
                                                     src={`/images/wappen/${rowData['b_land_iso']}.svg`}
                                                     width={20}
                                                     height={20}/>
                    <div className={'my-auto'}>{rowData['b_land_name']}</div>
                </div>);
            },
        }, {
            field: 'land_name',
            header: t('globalData:parcels.countryIso'),
            filter: true,
            filterField: 'land_iso',
            filterDropdownValues: process ? getGroupedItems(process, 'land_iso', 'land_name') : undefined,
            showFilterMatchModes: false,
            display: false,
            body: (rowData: any) => {
                return (<div className={'flex'}>
                    {rowData['land_name'] && <img className={'mr-1'}
                                                  src={`/images/wappen/${rowData['land_name'].toLowerCase()}.svg`}
                                                  width={20}
                                                  height={20}/>}
                    <div className={'my-auto'}>{rowData['land_name']}</div>
                </div>);
            },
        },);

    if (showPersons) {
        temp.push({
            field: 'person',
            header: t('globalData:global.anzahlpersonen'),
            body: (rowData: any) => {
                if (rowData['person']) {
                    return rowData['person'][0] === null ? 0 : rowData['person'].length;
                }
            },
            sortFunction: sortColumn
        },);
    }

    if (showParcels) {
        temp.push({
            field: 'flurstueck',
            header: t('globalData:global.anzahlflurstuecke'),
            body: (rowData: any) => {
                if (rowData['flurstueck']) {
                    return rowData['flurstueck'][0] === null ? 0 : rowData['flurstueck'].length;
                }
            },
            sortFunction: sortColumn,
            display: true
        },);
    }

    if (showLandRegisters) {
        temp.push({
            field: 'grundbuch',
            header: t('globalData:global.landRegisters'),
            body: (rowData: any) => {
                if (rowData['grundbuch']) {
                    return rowData['grundbuch'][0] === null ? 0 : rowData['grundbuch'].length;
                }
            },
            sortFunction: sortColumn,
            display: false
        },);
    }

    return temp;
};

export const getFiltersProcess = () => {
    return {
        'active': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'status_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}]
        },
        'dmt_es_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'b_land_iso': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'land_iso': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.IN}]
        },
        'kommentar': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'vorhabentraeger_id': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
        'zeitstempel': {
            operator: FilterOperator.AND,
            constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        },
    };
};

export const getColumnsCorrespondence = (t: any, correspondenceTypes: any[]) => {
    const temp: any[] = [];

    temp.push({
            field: 'korrespondenz_type_id',
            header: t('globalData:columns.korrespondenz_type_id'),
            filter: true,
            filterField: 'korrespondenz_type_id',
            filterDropdownValues: correspondenceTypes ? correspondenceTypes.map((item) => {
                return {value: item.value, name: item.label};
            }) : [],
            display: true,
            body: (rowData: any) => {
                return correspondenceTypeBody(rowData, correspondenceTypes);
            }
        },
        getColumn(t, '4', 'globalData:columns.'),
        getColumn(t, 'datum_soll', 'globalData:columns.', true, true, ColumnObjectType.Date, t('dateformat')),
        getColumn(t, 'datum_ist', 'globalData:columns.', true, true, ColumnObjectType.Date, t('dateformat')),
        getColumn(t, 'zeitstempel', 'globalData:columns.', true, true, ColumnObjectType.Date)
    );

    return temp;
};

export const getFiltersCorrespondence = () => {
    return {
        ...getFilter('korrespondenz_type_id'),
        ...getFilter('status_id'),
        ...getFilter('datum_soll'),
        ...getFilter('datum_ist'),
        ...getFilter('zeitstempel'),
        // 'amtsgericht': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
    };
};

export const getColumnsContract = (t: any, contractTypes: any[]) => {
    const temp: any[] = [];

    temp.push({
            field: 'vertrag_type_id',
            header: t('globalData:columns.vertrag_type_id'),
            filter: true,
            filterField: 'vertrag_type_id',
            filterDropdownValues: contractTypes ? contractTypes.map((item) => {
                return {value: item.value, name: item.label};
            }) : [],
            display: true,
            body: (rowData: any) => {
                return contractTypeBody(rowData, contractTypes);
            }
        },
        getColumn(t, 'status_id', 'globalData:columns.'),
        getColumn(t, 'datum_soll', 'globalData:columns.', true, true, ColumnObjectType.Date, t('dateformat')),
        getColumn(t, 'datum_ist', 'globalData:columns.', true, true, ColumnObjectType.Date, t('dateformat')),
        getColumn(t, 'flaeche_erwerb', 'globalData:columns.'),
        getColumn(t, 'flaeche_dinglich', 'globalData:columns.'),
        getColumn(t, 'flaeche_temp', 'globalData:columns.'),
        getColumn(t, 'zeitstempel', 'globalData:columns.', true, true, ColumnObjectType.Date)
    );

    return temp;
};

export const getFiltersContract = () => {
    return {
        ...getFilter('vertrag_type_id'),
        ...getFilter('status_id'),
        ...getFilter('datum_soll'),
        ...getFilter('datum_ist'),
        ...getFilter('flaeche_erwerb'),
        ...getFilter('flaeche_dinglich'),
        ...getFilter('flaeche_temp'),
        ...getFilter('zeitstempel'),
        // 'amtsgericht': {
        //     operator: FilterOperator.AND,
        //     constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]
        // },
    };
};

// export const getStatusLightPersonTest = () => {
//     let tempList: any[] = [];
//     let light = false;
//
//     tempList.push(<div className={'grid col-12'}>
//         <StatusLights values={[]} light={light} horizontal={true} backgroundColor={'cyan'}/>
//     </div>);
//
//     tempList.push(<div className={'grid col-12'}>
//         <StatusLights values={[]} light={light} horizontal={true} backgroundColor={'mediumslateblue'}/>
//     </div>);
//
//     for (let i = 0; i < 3; i++) {
//         tempList.push(<div className={'grid col-12'}>
//             <StatusLights values={[i]} light={light} horizontal={true}/>
//         </div>);
//     }
//
//     light = true;
//     for (let i = 0; i < 3; i++) {
//         tempList.push(<div className={'grid col-12'}>
//             <StatusLights values={[i]} light={light} horizontal={true}/>
//         </div>);
//     }
//
//     light = false;
//     for (let i = 0; i < 3; i++) {
//         tempList.push(<div className={'grid col-12'}>
//             <StatusLights values={[i]} light={light} horizontal={true} />
//         </div>);
//     }
//
//     light = true;
//     for (let i = 0; i < 3; i++) {
//         tempList.push(<div className={'grid col-12'}>
//             <StatusLights values={[i]} light={light} horizontal={true} />
//         </div>);
//     }
//
//     tempList.push(<div className={'grid col-12'}>
//         <StatusLights values={[0]} light={false} horizontal={true} backgroundColor={'red'}/>
//     </div>);
//
//     tempList.push(<div className={'grid col-12'}>
//         <StatusLights values={[1]} light={false} horizontal={true} backgroundColor={'yellow'}/>
//     </div>);
//
//     tempList.push(<div className={'grid col-12'}>
//         <StatusLights values={[2]} light={false} horizontal={true} backgroundColor={'chartreuse'}/>
//     </div>);
//
//     return tempList;
// };


export const onClickSetSelectionStatus = (project_id: string, status_id: number, type: string, selection: any[], t: any, setStatus: (obj: any) => void) => {
    CustomConfirmDialog({
        message: t('globalData:dialogs.activateSelection'),
        header: t('confirmDialog:activateSelectionDialogHeader'),
        translation: t,
        onConfirm: () => {
            switch (type.toLowerCase()) {
                case 'person':
                    for (let i = 0; i < selection.length; i++) {
                        const item = selection[i];
                        setStatus({
                            project_id: project_id,
                            status: status_id,
                            status_type: type,
                            person_id: item.person_id
                        });
                    }
                    break;

                case 'flurstueck':
                case 'parcel':
                    for (let i = 0; i < selection.length; i++) {
                        const item = selection[i];
                        setStatus({
                            project_id: project_id,
                            status: status_id,
                            status_type: type,
                            flurstueck_id: item.flurstueck_id
                        });
                    }
                    break;

                case 'grundbuch':
                case 'landregister':
                    for (let i = 0; i < selection.length; i++) {
                        const item = selection[i];
                        setStatus({
                            project_id: project_id,
                            status: status_id,
                            status_type: type,
                            grundbuch_id: item.grundbuch_id
                        });
                    }
                    break;
            }
        }
    });
};

export const moveComments = (getValues: any, setValue: any) => {
    const kommentar = getValues('kommentar');

    if (kommentar && kommentar.length > 0) {
        let kommentare = getValues('kommentare');

        if (!kommentare) {
            kommentare = [];
        }

        for (let i = 0; i < kommentar.length; i++) {
            const k = kommentar[i];

            kommentare.unshift(k);
        }

        setValue('kommentare', kommentare);
        setValue('kommentar', []);
    }
};

export const addValues = (field: string, mappings: any[], setValues: (prevState: any) => void) => {
    setValues((prevState: any) => {
        const temp = [...prevState];

        if (temp) {
            for (let i = 0; i < mappings.length; i++) {
                const map = mappings[i];

                if (map) {
                    const item = temp.find((x: any) => x[field] === map.id);

                    if (item) {
                        if (map.remove) {
                            temp.splice(temp.indexOf(item), 1);
                        }
                    }
                }
            }
        }
        return temp;
    });
};

export const getMappingContent = (projectId: string, selectedValue: any, idType: string, mappingId: string, mappingType: MappingType, setMappingInputs: (item: any) => void, setValidationError: (hasError: boolean) => void) => {
    if (selectedValue) {
        let personType: number = 0;

        let anteil: string = '';
        let mitanteil: string = '';
        let recht: string = '';

        let bvnr: number = 0;
        let gr_ha: number = 0;
        let gr_a: number = 0;
        let gr_m2: number = 0;

        let lfdnr_eintragung: string = '';
        let lfdnr_bestandsverzeichnis: string = '';
        let grundlage_eintragung: string = '';
        let ehepartner: string = '';
        let erbengemeinschaft: any[] = [];

        let korrespondenz_type_id: number = 0;
        let vertrag_type_id: number = 0;

        let datum_soll: string = '';
        let datum_ist: string = '';

        let flaeche_erwerb: number = 0;
        let flaeche_dinglich: number = 0;
        let flaeche_temp: number = 0;

        if (mappingId !== '') {
            personType = selectedValue.person_type_id != null ? selectedValue.person_type_id : 0;
            anteil = selectedValue.anteileigentuemer != null ? selectedValue.anteileigentuemer : '';
            mitanteil = selectedValue.miteigentumsanteil != null ? selectedValue.miteigentumsanteil : '';
            recht = selectedValue.artrechtsgemeinschaft != null ? selectedValue.artrechtsgemeinschaft : '';

            bvnr = selectedValue.bvnr != null ? selectedValue.bvnr : 0;
            gr_ha = selectedValue.gr_ha != null ? selectedValue.gr_ha : 0;
            gr_a = selectedValue.gr_a != null ? selectedValue.gr_a : 0;
            gr_m2 = selectedValue.gr_m2 != null ? selectedValue.gr_m2 : 0;

            lfdnr_eintragung = selectedValue.lfdnr_eintragung != null ? selectedValue.lfdnr_eintragung : '';
            lfdnr_bestandsverzeichnis = selectedValue.lfdnr_bestandsverzeichnis != null ? selectedValue.lfdnr_bestandsverzeichnis : '';
            grundlage_eintragung = selectedValue.grundlage_eintragung != null ? selectedValue.grundlage_eintragung : '';
            ehepartner = selectedValue.ehepartner != null ? selectedValue.ehepartner : '';
            erbengemeinschaft = selectedValue.erbengemeinschaft != null ? selectedValue.erbengemeinschaft : [];

            korrespondenz_type_id = selectedValue.korrespondenz_type_id != null ? selectedValue.korrespondenz_type_id : 0;
            vertrag_type_id = selectedValue.vertrag_type_id != null ? selectedValue.vertrag_type_id : 0;

            datum_soll = selectedValue.datum_soll != null ? selectedValue.datum_soll : null;
            datum_ist = selectedValue.datum_ist != null ? selectedValue.datum_ist : null;

            flaeche_erwerb = selectedValue.flaeche_erwerb != null ? selectedValue.flaeche_erwerb : 0;
            flaeche_dinglich = selectedValue.flaeche_dinglich != null ? selectedValue.flaeche_dinglich : 0;
            flaeche_temp = selectedValue.flaeche_temp != null ? selectedValue.flaeche_temp : 0;
        }

        return (<MappingInputs id={idType} projectId={projectId}
                               mappingType={mappingType}
                               mappings={{
                                   map_person_type_id: personType,
                                   map_anteileigentuemer: anteil,
                                   map_miteigentumsanteil: mitanteil,
                                   map_artrechtsgemeinschaft: recht,
                                   map_bvnr: bvnr,
                                   map_gr_ha: gr_ha,
                                   map_gr_a: gr_a,
                                   map_gr_m2: gr_m2,
                                   map_lfdnr_eintragung: lfdnr_eintragung,
                                   map_lfdnr_bestandsverzeichnis: lfdnr_bestandsverzeichnis,
                                   map_grundlage_eintragung: grundlage_eintragung,
                                   map_ehepartner: ehepartner,
                                   map_erbengemeinschaft: erbengemeinschaft,
                                   map_korrespondenz_type_id: korrespondenz_type_id,
                                   map_datum_soll: datum_soll,
                                   map_datum_ist: datum_ist,
                                   map_vertrag_type_id: vertrag_type_id,
                                   map_flaeche_erwerb: flaeche_erwerb,
                                   map_flaeche_dinglich: flaeche_dinglich,
                                   map_flaeche_temp: flaeche_temp
                               }}
                               onChange={(mappings: any) => {
                                   setMappings(projectId, selectedValue, mappings, mappingType, setMappingInputs);
                               }}
                               setValidationError={setValidationError}
                               activeMappings={[]}
                               autoCompleteComponent={undefined}
                               edit={true}/>);
    }
};

export const assignMappingGlobal = (type: string, selectedValue: any, mappingInputs: any, mappingType: MappingType, dispatch: (arg: any) => void, setValues: (item: any) => void) => {
    if (selectedValue && Object.keys(mappingInputs).length) {
        setValues((prevState: any[]) => {
            const temp = JSON.parse(JSON.stringify(prevState));

            for (let i = 0; i < temp.length; i++) {
                const p = temp[i];

                if (p[type] && selectedValue[type] && p[type] === selectedValue[type]) {
                    p.person_type_id = mappingInputs.person_type_id;

                    switch (mappingType) {
                        case MappingType.personLandRegister :
                            p.anteileigentuemer = mappingInputs.anteileigentuemer;
                            p.miteigentumsanteil = mappingInputs.miteigentumsanteil;
                            p.artrechtsgemeinschaft = mappingInputs.artrechtsgemeinschaft;
                            p.lfdnr_eintragung = mappingInputs.lfdnr_eintragung;
                            p.lfdnr_bestandsverzeichnis = mappingInputs.lfdnr_bestandsverzeichnis;
                            p.grundlage_eintragung = mappingInputs.grundlage_eintragung;
                            p.ehepartner = mappingInputs.ehepartner;
                            break;
                        case MappingType.landRegisterParcel:
                            p.bvnr = mappingInputs.bvnr;
                            p.gr_ha = mappingInputs.gr_ha;
                            p.gr_a = mappingInputs.gr_a;
                            p.gr_m2 = mappingInputs.gr_m2;
                            break;
                        case MappingType.personCorrespondence:
                            p.korrespondenz_type_id = mappingInputs.korrespondenz_type_id;
                            p.datum_soll = mappingInputs.datum_soll;
                            p.datum_ist = mappingInputs.datum_ist;
                            break;
                        case MappingType.parcelContract:
                            p.vertrag_type_id = mappingInputs.vertrag_type_id;
                            p.datum_soll = mappingInputs.datum_soll;
                            p.datum_ist = mappingInputs.datum_ist;
                            p.flaeche_erwerb = mappingInputs.flaeche_erwerb;
                            p.flaeche_dinglich = mappingInputs.flaeche_dinglich;
                            p.flaeche_temp = mappingInputs.flaeche_temp;
                            break;
                    }
                    break;
                }
            }

            dispatch(addMappings(mappingInputs));
            return temp;
        });
    }

    // hideDialog();
};

export const setMappings = (projectId: string, selectedValue: any, mapping: any, mappingType: MappingType, setMappingInputs: (item: any) => void) => {
    if (selectedValue) {
        setMappingInputs({
            person_type_id: mapping.person_type_id,
            anteileigentuemer: mapping.anteileigentuemer,
            miteigentumsanteil: mapping.miteigentumsanteil,
            artrechtsgemeinschaft: mapping.artrechtsgemeinschaft,
            lfdnr_eintragung: mapping.lfdnr_eintragung,
            lfdnr_bestandsverzeichnis: mapping.lfdnr_bestandsverzeichnis,
            grundlage_eintragung: mapping.grundlage_eintragung,
            ehepartner: mapping.ehepartner,
            erbengemeinschaft: mapping.erbengemeinschaft,
            bvnr: mapping.bvnr,
            gr_ha: mapping.gr_ha,
            gr_a: mapping.gr_a,
            gr_m2: mapping.gr_m2,
            project_id: projectId,
            common_data: true,
            mapping_type: mappingType,
            korrespondenz_type_id: mapping.korrespondenz_type_id,
            datum_soll: mapping.datum_soll,
            datum_ist: mapping.datum_ist,
            flaeche_erwerb: mapping.flaeche_erwerb,
            flaeche_dinglich: mapping.flaeche_dinglich,
            flaeche_temp: mapping.flaeche_temp,
            vertrag_type_id: mapping.vertrag_type_id,
        });
    }
};

export const getPersonLabel = (person: Person): string => {
    let result = '';

    if (person.ist_juristisch) {
        result = person.name;

        if (person.adresse) {
            for (let i = 0; i < person.adresse.length; i++) {
                let adresse = person.adresse[i];

                if (adresse.adresse_type_id === 6 && adresse.ladungsadresse) {
                    result += ' (' + adresse.ort + ', ' + adresse.strassehausnummer + ')';
                    break;
                }
            }
        }
    } else {
        result = person.name;

        if (person.vorname) {
            result += ', ' + person.vorname;
        }

        if (person.adresse) {
            for (let i = 0; i < person.adresse.length; i++) {
                let adresse = person.adresse[i];

                if (adresse.adresse_type_id === 1 && adresse.ladungsadresse) {
                    result += ' (' + adresse.ort + ', ' + adresse.strassehausnummer + ')';
                    break;
                }
            }
        }
    }

    return result;
};
